import {type TranslationObject} from '@colonist/i18n'
import {delay, TimeUtils} from '@colonist/utils'
import {UITopNotificationType} from '../notifications/UINotificationController.ts'
import {globalClientVariables, notificationController} from '../EntryShared.ts'
import {type TimeoutId} from '@colonist/types'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables.ts'
import {logError} from '../ui_utils/Utils.ts'
import {UIReconnectingBackgroundView} from './UIReconnectingBackgroundView.ts'
import {KeyBindController} from '../KeyBindController.ts'

export type ReconnectResult = 'connected' | 'retry' | 'permanentlyFailed'
export type ReconnectFunction = () => Promise<ReconnectResult>

export class UIReconnectionController {
    private static readonly MAX_RECONNECT_ATTEMPTS = 15

    private isReconnecting: boolean = false
    private isEnabled: boolean
    private hideConnect: boolean = false

    private maxDisconnectTimeout: TimeoutId | undefined

    private timeDisconnected: number | undefined

    private readonly reconnectFunction: ReconnectFunction
    private readonly canReconnectFunction: () => boolean

    constructor(reconnectFunction: ReconnectFunction, canReconnectFunction: () => boolean, hideFirstConnect: boolean = false) {
        this.isEnabled = true
        this.reconnectFunction = reconnectFunction
        this.canReconnectFunction = canReconnectFunction
        this.hideConnect = hideFirstConnect
    }

    startReconnecting() {
        if(!this.isEnabled) return
        if(this.isReconnecting) return
        this.isReconnecting = true

        notificationController?.removeDisconnectBanners()

        if(!this.hideConnect && !this.canReconnectFunction()) {
            this.displayReconnectBanner()
            this.disable()
            return
        }

        const delayBeforeTellingUserToRefreshBrowser = 30 * TimeUtils.MILLISECONDS
        clearTimeout(this.maxDisconnectTimeout)
        this.maxDisconnectTimeout = setTimeout(() => {
            this.displayReconnectBanner()
        }, delayBeforeTellingUserToRefreshBrowser)

        this.timeDisconnected = Date.now()
        if(!this.hideConnect) UIReconnectingBackgroundView.show({key: 'strings:game.gameState.gameLogs.gameDisconnected.reconnecting'})

        this.hideConnect = false

        void this.reconnect()
    }

    private async reconnect() {
        const reconnectAttemptDelay = 3 * TimeUtils.MILLISECONDS

        let attempt = 1
        while(attempt <= UIReconnectionController.MAX_RECONNECT_ATTEMPTS) {
            const reconnectResult = await this.reconnectFunction()
            switch(reconnectResult) {
                case 'connected':
                    this.connectionReestablished()
                    return
                case 'retry':
                    await delay(reconnectAttemptDelay)
                    attempt += 1
                    break
                case 'permanentlyFailed':
                    this.removeUI()
                    this.displayReconnectBanner()

                    this.isReconnecting = false

                    return
            }
        }
    }

    private connectionReestablished() {
        this.isReconnecting = false
        clearTimeout(this.maxDisconnectTimeout)
        KeyBindController.instance.clearAllKeyBinds()
        this.removeWarnings()
    }

    private displayReconnectBanner() {
        const isNonBrowserEnvironment = UIUtilsEjsVariables.isUserOnDiscord || UIUtilsEjsVariables.isUserOnMobileAppOrWeb
        const context = isNonBrowserEnvironment ? 'activity' : 'browser'
        const text: TranslationObject = {key: 'strings:homePage.loadingError.prompt', options: {context: context}}

        if(UIUtilsEjsVariables.socketDebugActive) logError('connect', [text])

        if(globalClientVariables.serverUpdated) {
            const updateMessage: TranslationObject = {
                key: 'strings:popups.errors.newVersion.newVersionReleasedWarningLostConnection',
            }
            notificationController?.show(updateMessage, UITopNotificationType.SocketError, updateMessage, undefined, undefined, false)
        } else {
            notificationController?.show(text, UITopNotificationType.SocketError, {key: 'strings:colonistCommon.buttons.reload'}, () => location.reload(), undefined, false)
        }
        clearTimeout(this.maxDisconnectTimeout)
    }

    private removeWarnings() {
        const timeDisconnected = this.timeDisconnected ?? Date.now()

        const minimumTimeToDisplayReconnectScreenForBetterUX = 1 * TimeUtils.MILLISECONDS
        const elapsedTime = Date.now() - timeDisconnected
        const remainingTime = minimumTimeToDisplayReconnectScreenForBetterUX - elapsedTime

        setTimeout(() => {
            this.removeUI()
        }, remainingTime)

        this.timeDisconnected = undefined
    }

    // eslint-disable-next-line class-methods-use-this
    private removeUI() {
        UIReconnectingBackgroundView.remove()
        notificationController?.removeSocketErrorBanners()
    }

    disable() {
        this.isEnabled = false
    }
}
